import { useEffect } from 'react';

/**
 * Activates the .reveal scroll-in animation for the current page.
 * Use on pages that are NOT wrapped by <Layout/> (Layout already handles this).
 *
 * Usage:
 *   const Page = () => {
 *     useRevealObserver();
 *     return (<section><div className="reveal">...</div></section>);
 *   };
 */
const useRevealObserver = () => {
  useEffect(() => {
    // Defer one frame so all .reveal elements are in the DOM
    const id = requestAnimationFrame(() => {
      const elements = document.querySelectorAll('.reveal:not(.is-visible)');
      // If IntersectionObserver isn't supported, just show all
      if (typeof IntersectionObserver === 'undefined') {
        elements.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
      );
      elements.forEach((el) => obs.observe(el));

      // Safety: if user lands deep in page or above-fold elements are visible, show them immediately
      elements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('is-visible');
          obs.unobserve(el);
        }
      });
    });

    return () => cancelAnimationFrame(id);
  }, []);
};

export default useRevealObserver;
